import { createAsyncThunk } from "@reduxjs/toolkit"
import { setFollowingEntity } from "plugins/shared/playback/state/slice"
import { selectFirePerimeterById } from "features/entity-caching/fire-perimeters/selectors"
import { get } from "nodash"
import { PartialViewState } from "../control/types"
import { BoundaryBoxCoordinates } from "../types"
import {
    reduceAllPointsToBoundaryBox,
    reduceLayeredHotspotsToCoordinatePointsMap,
    reduceLayeredPerimeterToCoordinatePointsMap,
} from "./module"
import { calculateTextZoomLevel } from "plugins/Toolbox/Text/module"
import { Point } from "@nova/data-create-polygon-kml"
import { RootState } from "integrations/redux/rootReducer"
import { selectHotspotsByFlightId } from "features/entity-caching/hotspots/selectors"
import { UploadState } from "@nova/slice-multifile-upload"
import { isUsable } from "@nova/constants"

type ZoomToShowActionPayload = {
    boundaryBox?: BoundaryBoxCoordinates
    nextViewState?: PartialViewState
    padding?: number
    preserveZoom?: boolean
}

/**
 * Zooms to show the fire perimeter on the map. NOTE: This does not show the "dots"
 *
 * @param {Object} payload - Payload object containing parameters.
 * @param {string} [payload.firePerimeterId] - Identifier for the fire perimeter.
 * @param {string} payload.fireId - Identifier for the fire.
 * @param {boolean} [payload.force] - Whether to force zooming even if a previous view state exists.
 *
 * @returns {Promise<ZoomToShowActionPayload>} - A Promise that resolves with the action payload.
 *
 * @throws {Error} - If there is an error during the zoom operation.
 */
export const zoomToShowFirePerimeter = createAsyncThunk<
    ZoomToShowActionPayload,
    {
        firePerimeterId?: string
        fireId: string
        force?: boolean
    }
>("map/zoom/showingFirePerimeter", async (payload, api) => {
    api.dispatch(setFollowingEntity(undefined))
    const state = api.getState() as RootState
    const firePerimeterId = get(payload, "firePerimeterId") as string

    const perimetersCoordinatesMap = reduceLayeredPerimeterToCoordinatePointsMap({
        perimeter: selectFirePerimeterById(firePerimeterId)(state),
    })

    const boundaryBox = reduceAllPointsToBoundaryBox([perimetersCoordinatesMap])

    if (boundaryBox) {
        return { boundaryBox }
    }

    return {}
})

/**
 * Zooms to show the specified points on the map.
 *
 * @param {Object} payload - Payload object containing parameters.
 * @param {Array<[number, number]>} payload.points - Array of points represented as [longitude, latitude].
 *
 * @returns {Promise<ZoomToShowActionPayload>} - A Promise that resolves with the action payload.
 *
 * @throws {Error} - If there is an error during the zoom operation.
 */
export const zoomToShowPoints = createAsyncThunk<
    ZoomToShowActionPayload,
    {
        points: [number, number][]
        padding?: number
        preserveZoom?: boolean
    }
>("map/zoom/showingPoints", async (payload, api) => {
    api.dispatch(setFollowingEntity(undefined))
    const points = payload.points

    const perimetersCoordinatesMap = {
        longitudes: points.map((e) => e[0]),
        latitudes: points.map((e) => e[1]),
    }
    const boundaryBox = reduceAllPointsToBoundaryBox([perimetersCoordinatesMap])
    if (boundaryBox) {
        return { boundaryBox, padding: payload.padding, preserveZoom: payload.preserveZoom }
    }

    return {}
})

export const zoomToShowText = createAsyncThunk<
    { point: Point; zoomLevel: number },
    {
        point: [number, number]
        fontSize: number
        latitude: number
    }
>("map/zoom/zoomToShowText", async (payload, api) => {
    api.dispatch(setFollowingEntity(undefined))
    const { point, fontSize, latitude } = payload
    const zoomLevel = calculateTextZoomLevel(fontSize, 18, latitude)
    return { point, zoomLevel: zoomLevel }
})

/**
 * Zooms to show the hotspots along the flight path on the map.
 *
 * @param {Object} payload - Payload object containing parameters.
 * @param {string} [payload.flightId] - Identifier for the flight.
 *
 * @returns {Promise<ZoomToShowActionPayload>} - A Promise that resolves with the action payload.
 *
 * @throws {Error} - If there is an error during the zoom operation.
 */
export const zoomToShowHotspotsInFlightPath = createAsyncThunk<
    ZoomToShowActionPayload,
    {
        flightId?: string
    }
>("map/zoom/showingFlightHotspots", async (payload, api) => {
    api.dispatch(setFollowingEntity(undefined))
    const state = api.getState() as RootState
    const flightId = get(payload, "flightId") as string

    if (!flightId) {
        return {}
    }

    const coordinatesMap = reduceLayeredHotspotsToCoordinatePointsMap({
        allItems: selectHotspotsByFlightId(flightId)(state),
    })

    const boundaryBox = reduceAllPointsToBoundaryBox([coordinatesMap])

    if (boundaryBox) {
        return { boundaryBox }
    }

    return {}
})

export const zoomToShowFlightPath = createAsyncThunk(
    "map/zoom/showingFlightPath",
    async (uploads: UploadState<{ longitude?: number; latitude?: number; id: string }>[], api) => {
        api.dispatch(setFollowingEntity(undefined))
        if (!uploads) return
        const latitudes = [] as number[]
        const longitudes = [] as number[]
        uploads.forEach((upload) => {
            const usable = isUsable(upload)
            if (usable && usable.longitude && usable.latitude) {
                latitudes.push(usable.latitude)
                longitudes.push(usable.longitude)
            }
        })

        const boundaryBox = {
            minLatitude: Math.min(...latitudes) || 0,
            minLongitude: Math.min(...longitudes) || 0,
            maxLatitude: Math.max(...latitudes) || 0,
            maxLongitude: Math.max(...longitudes) || 0,
        }
        return boundaryBox
    }
)
